import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import { convertToDate, useVolumeTrendsReport } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dateToString, formatDate } from 'utils/helpers';

import { AxiosError } from 'axios';
import { createReportURL } from 'core/routes';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import {
    VOLUME_TRENDS_REPORT,
    VOLUME_TRENDS_REPORT_TITLE,
    columns,
} from './constants';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportGridContainer } from 'components/ReportGridContainer';
import { ReportSearchForm } from 'components/ReportForms/ReportSearchForm';
import { ReportProps } from 'pages/ReportsDetails/types';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import DevExtremeReportGrid from 'components/ReportGrid_New';


export function VolumeTrendsReport({
    reportPath,
    setErrorMessage,
    setToast,
}: ReportProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const urlParamsValue = {
        endDate: urlParams.get('endDate'),
        startDate: urlParams.get('startDate'),
    };

    const queryEnabled = !!urlParamsValue.startDate && !!urlParamsValue.endDate;

    const { isReportFetching, refetchReport, reportData } = useVolumeTrendsReport(
        {
            startDate: urlParamsValue.startDate,
            endDate: urlParamsValue.endDate,
            enabled: queryEnabled,
            onError: handleError,
        }
    );

    const rows = useMemo(() => reportData, [reportData]);

    const [startDate, setStartDate] = useState<Date | null>(
        convertToDate(urlParamsValue.startDate) ?? null
    );
    const [endDate, setEndDate] = useState<Date | null>(
        convertToDate(urlParamsValue.endDate) ?? null
    );

    useEffect(() => {
        navigateIfPossible();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlParamsValue.startDate, urlParamsValue.endDate]);

    const reportHeaderTagLine = useMemo(
        () =>
            `Report produced on ${formatDate(new Date())} from ${formatDate(startDate)} to ${formatDate(endDate)}`,
        [startDate, endDate]
    );

    return (
        <MainPageContainer
            isLoading={isReportFetching}
            direction="column"
            wrap="nowrap"
        >
            <UpperInfoContainer direction="column">
                <ReportBackButton />
                <ReportHeader
                    reportName={VOLUME_TRENDS_REPORT}
                    infoMessage="Displays the Volume Trends Report for the selected date range."
                />
                <ReportSearchForm
                    handleSubmit={handleSubmit}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    isDisabled={isReportFetching}
                />
            </UpperInfoContainer>

            {!isReportFetching && rows?.length === 0 && (
                <NoReportDataMessage />
            )}

            <ReportGridContainer direction="column">
                <DevExtremeReportGrid
                    columns={columns}
                    displayGroupingPanel
                    headerTitleForSheet={VOLUME_TRENDS_REPORT}
                    isLoading={isReportFetching}
                    reportHeaderTagLine={reportHeaderTagLine}
                    rows={rows}
                    title={VOLUME_TRENDS_REPORT_TITLE}
                />
            </ReportGridContainer>
        </MainPageContainer>
    );

    function handleSubmit(event: FormEvent) {
        event.preventDefault();
        if (
            urlParamsValue.endDate == dateToString(endDate) &&
            urlParamsValue.startDate == dateToString(startDate)
        ) {
            refetchReport();
        } else navigateIfPossible();
    }

    function navigateIfPossible() {
        if (!!startDate && !!endDate) {
            navigate(
                createReportURL(
                    {
                        startDate: dateToString(startDate),
                        endDate: dateToString(endDate),
                    },
                    reportPath,
                ), { replace: true });
        }
    }

    function handleError(error: AxiosError) {
        reportLogger(error);
        setErrorMessage(errorMessages.reports(VOLUME_TRENDS_REPORT));
        setToast(true);
    }
}

